import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useParams } from "react-router-dom";
import { CoachAvailabilityCalendar } from "@/components/CoachAvailabilityCalendar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, MapPin, Award, ArrowRight, Star, Users as UsersIcon, CalendarDays, ShieldCheck } from "lucide-react";
import { SPORT_META } from "@/context/SportContext";

const ACCENT = "#FF4500";

export default function PublicCoaches() {
  const { id } = useParams();
  const [coaches, setCoaches] = useState([]);
  const ctl = useListControls(coaches, { name: (c) => c.name, fields: (c) => [c.name, (c.coach_sports || []).join(" ")] });
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (id) {
      api.get(`/public/coaches/${id}`).then((r) => setProfile(r.data)).catch(() => setProfile({ notFound: true }));
    } else {
      api.get("/public/coaches?sport=pickleball").then((r) => setCoaches(r.data));
    }
  }, [id]);

  if (id) return <CoachProfileView profile={profile} />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg">
      <header className="px-8 py-6 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ background: ACCENT }}>
            <span className="text-black font-black text-lg">R</span>
          </div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </Link>
        <Link to="/login" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white">Sign in</Link>
      </header>

      <section className="max-w-7xl mx-auto px-8 py-16">
        <div className="label-mono mb-3" style={{ color: ACCENT }}>VERIFIED COACHES</div>
        <h1 className="h-display text-5xl md:text-6xl tracking-tighter mb-12">Find your pickleball coach.</h1>

        {!coaches.length ? (
          <div className="card-surface p-12 text-center">
            <Search className="w-10 h-10 text-[#262626] mx-auto mb-4" />
            <div className="text-sm text-[#A3A3A3]">No coaches yet.</div>
          </div>
        ) : (
          <>
          <ListControlsBar ctl={ctl} placeholder="Search coaches…" testid="public-coaches" className="mb-4" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
            {ctl.list.map((c) => (
              <Link key={c.id} to={`/coaches/${c.id}`} className="bg-[#0A0A0A] p-6 hover:bg-[#141414] transition-colors group relative" data-testid={`coach-card-${c.id}`}>
                {c.is_away && (
                  <span
                    className="absolute top-3 right-3 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                    style={{ background: "#FF9F1C", color: "#0A0A0A" }}
                    title={c.away_note || "Coach is currently away"}
                    data-testid={`coach-away-badge-${c.id}`}
                  >
                    Away
                  </span>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 flex items-center justify-center text-xl font-bold" style={{ background: ACCENT + "20", color: ACCENT }}>
                    {(c.name || "?")[0]}
                  </div>
                  {c.years_experience > 0 && <div className="text-right"><div className="label-mono text-[0.55rem]">YEARS</div><div className="h-display text-2xl" style={{ color: ACCENT }}>{c.years_experience}</div></div>}
                </div>
                <h3 className="h-display text-xl tracking-tight mb-1">{c.name}</h3>
                {c.headline && <div className="text-sm text-[#E5E5E5] mb-2">{c.headline}</div>}
                {c.certification && <div className="text-xs text-[#A3A3A3] mb-3 inline-flex items-center gap-1"><Award className="w-3 h-3" /> {c.certification}</div>}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(c.coach_sports || []).map((s) => (
                    <span key={s} className="px-2 py-0.5 text-[10px] uppercase tracking-wider border" style={{ borderColor: SPORT_META[s]?.color, color: SPORT_META[s]?.color }}>{s}</span>
                  ))}
                </div>
                <div className="text-xs uppercase tracking-wider inline-flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: ACCENT }}>
                  View profile <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
          </>
        )}
      </section>
    </div>
  );
}

function CoachProfileView({ profile }) {
  // iter104 — Consumes auth so the CTA can swap Sign up ↔ Book now.
  // Kept as a local hook inside the sub-component (rather than passed down)
  // so future public sub-views can drop in the same call without prop drilling.
  const { user } = useAuth();
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]"><div className="label-mono">Loading…</div></div>;
  if (profile.notFound) return <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]">Coach not found.</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg">
      <header className="px-8 py-6 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ background: ACCENT }}><span className="text-black font-black text-lg">R</span></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </Link>
        <Link to="/coaches" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white">All coaches</Link>
      </header>

      <section className="max-w-5xl mx-auto px-8 py-12">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="label-mono mb-2" style={{ color: ACCENT }}>COACH PROFILE</div>
            <h1 className="h-display text-5xl md:text-6xl tracking-tighter mb-4">{profile.name}</h1>
            {profile.is_away && (
              <div
                className="mb-4 px-4 py-3 flex items-start gap-3"
                style={{ background: "#FF9F1C15", borderLeft: "3px solid #FF9F1C" }}
                data-testid="public-coach-away-banner"
              >
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: "#FF9F1C" }}>Currently Away</div>
                {profile.away_note && <div className="text-sm text-[#E5E5E5]">— {profile.away_note}</div>}
              </div>
            )}
            {profile.headline && <p className="text-xl text-[#E5E5E5] mb-4">{profile.headline}</p>}
            {profile.bio && <p className="text-[#A3A3A3] leading-relaxed mb-6">{profile.bio}</p>}
            <div className="flex flex-wrap gap-2 mb-6">
              {(profile.coach_sports || []).map((s) => (
                <span key={s} className="px-3 py-1 text-xs uppercase tracking-wider border" style={{ borderColor: SPORT_META[s]?.color, color: SPORT_META[s]?.color }}>{s}</span>
              ))}
            </div>
            {/* iter104 — Conditional booking CTA. Logged-in users skip the
                sign-up funnel and go straight to /offerings where the actual
                booking flow lives. Anonymous visitors still land on /register
                so they can create an account first.
                iter105 (Slice C) — Away Mode disables the CTA and swaps the
                copy so students know the coach isn't accepting bookings. */}
            {profile.is_away ? (
              <button
                type="button"
                disabled
                data-testid="public-coach-book-away"
                className="px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 cursor-not-allowed opacity-70"
                style={{ background: "#525252", color: "#0A0A0A" }}
                title={profile.away_note || "Coach is currently away"}
              >
                Currently Away · Bookings paused
              </button>
            ) : user ? (
              <Link
                to="/offerings"
                data-testid="public-coach-book-now"
                className="px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                style={{ background: ACCENT, color: "#0A0A0A" }}
              >
                Book now <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : (
              <Link
                to="/register"
                data-testid="public-coach-signup"
                className="px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                style={{ background: ACCENT, color: "#0A0A0A" }}
              >
                Sign up to book <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <CoachAvailabilityCalendar coachId={profile.id} />
          <div className="card-surface p-6 space-y-3 text-sm" data-testid="public-coach-credentials">
            <div className="label-mono mb-2">CREDENTIALS</div>
            {profile.certification && <div><Award className="w-3.5 h-3.5 inline mr-1 text-[#CCFF00]" /> {profile.certification}{profile.cert_body ? ` (${profile.cert_body})` : ""}</div>}
            {profile.years_experience > 0 && <div><Star className="w-3.5 h-3.5 inline mr-1 text-[#CCFF00]" /> {profile.years_experience} years coaching</div>}
            <div className="pt-1 space-y-1.5">
              <div
                className={`flex items-center gap-2 text-xs ${profile.certification_verified ? "text-[#CCFF00]" : "text-[#525252]"}`}
                data-testid="public-cert-verified"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {profile.certification_verified ? "Certification verified by Rally Lab admin" : "Certification not yet verified"}
              </div>
              <div
                className={`flex items-center gap-2 text-xs ${profile.insurance_verified ? "text-[#CCFF00]" : "text-[#525252]"}`}
                data-testid="public-ins-verified"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {profile.insurance_verified ? `Insurance in date${profile.insurance_provider ? ` — ${profile.insurance_provider}` : ""}` : "Insurance not on file"}
              </div>
            </div>
            {(profile.venues || []).length > 0 && (
              <div>
                <div className="label-mono mb-1 pt-2">Venues</div>
                {profile.venues.map((v) => <div key={v} className="text-xs text-[#A3A3A3] flex items-center gap-1"><MapPin className="w-3 h-3" /> {v}</div>)}
              </div>
            )}
          </div>
          {/* Coach-specific pricing */}
          {(() => {
            const p = profile.pricing || {};
            const fallback1on1 = p.one_on_one ?? profile.hourly_rate;
            const has = (p.group || p.performance || fallback1on1);
            if (!has) return null;
            return (
              <div className="card-surface p-6 space-y-2 text-sm" data-testid="coach-pricing-public">
                <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>{profile.name?.split(" ")[0]?.toUpperCase()}'S RATES</div>
                {p.group ? <div className="flex justify-between"><span className="text-[#A3A3A3]">Group · per player</span><span className="font-semibold" style={{ color: "#007AFF" }}>£{p.group}</span></div> : null}
                {p.performance ? <div className="flex justify-between"><span className="text-[#A3A3A3]">Performance package</span><span className="font-semibold" style={{ color: "#FF4500" }}>£{p.performance}</span></div> : null}
                {fallback1on1 ? <div className="flex justify-between"><span className="text-[#A3A3A3]">1:1 · per hour</span><span className="font-semibold" style={{ color: "#CCFF00" }}>£{fallback1on1}</span></div> : null}
                <div className="text-[10px] text-[#525252] uppercase tracking-wider pt-2">Coach-set rates. Booking on offerings below uses each session's individual price.</div>
              </div>
            );
          })()}
        </div>

        <div>
          <div className="label-mono mb-4 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-[#CCFF00]" /> UPCOMING SESSIONS WITH {profile.name?.split(" ")[0]?.toUpperCase()}</div>
          {!(profile.offerings || []).length ? (
            <div className="text-sm text-[#A3A3A3]">No upcoming sessions published.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-px bg-[#262626]">
              {profile.offerings.map((o) => {
                const d = new Date(o.start);
                return (
                  <div key={o.id} className="bg-[#0A0A0A] p-5">
                    <div className="flex justify-between mb-2">
                      <div className="label-mono" style={{ color: SPORT_META[o.sport]?.color }}>{o.sport}</div>
                      <div className="h-display text-xl" style={{ color: ACCENT }}>£{o.price_per_player}</div>
                    </div>
                    <div className="font-semibold text-sm mb-1">{o.title}</div>
                    <div className="text-xs text-[#A3A3A3]">{d.toLocaleString(undefined, { weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit"})} · {o.venue}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1"><UsersIcon className="w-3 h-3 inline" /> {o.spaces_left} spaces</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
